import React from 'react';
import { useSEO } from '@/hooks/useSEO';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowDown } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const HERO_IMG = 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=2000&q=80&auto=format&fit=crop';

const SkylineImg = 'https://images.unsplash.com/photo-1460317442991-0ec209397118?w=2000&q=80&auto=format&fit=crop';

const Home = () => {
  const { t } = useLanguage();
  useSEO({ title: 'UAB MAIŠIAGALA GROUP — Investicijos ir nekilnojamasis turtas', description: 'Privati investicinė ir nekilnojamojo turto plėtros įmonė Lietuvoje. Komercinis ir gyvenamasis NT, plėtra, turto valdymas.', path: '/' });
  const areas = t('home.areas') || [];
  const advantages = t('home.advantages') || [];

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative min-h-[100vh] w-full overflow-hidden" data-testid="home-hero">
        <div
          className="absolute inset-0 bg-cover bg-center anim-scale-out"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 min-h-[100vh] flex flex-col justify-end pb-24 pt-32">
          <div className="anim-fade-up delay-200">
            <div className="text-xs uppercase tracking-[0.32em] text-gold mb-6" data-testid="hero-tagline">
              {t('home.tagline')}
            </div>
          </div>
          <h1
            className="anim-fade-up delay-300 font-serif font-light text-white text-5xl sm:text-6xl lg:text-7xl tracking-tighter max-w-4xl leading-[1.05]"
            data-testid="hero-title"
          >
            {t('home.hero_title')}
          </h1>
          <p
            className="anim-fade-up delay-400 mt-8 text-white/80 max-w-xl text-base sm:text-lg leading-relaxed"
            data-testid="hero-subtitle"
          >
            {t('home.hero_sub')}
          </p>
          <div className="anim-fade-up delay-500 mt-12 flex items-center gap-6">
            <Link
              to="/contact"
              data-testid="hero-cta"
              className="group inline-flex items-center gap-3 bg-gold text-black px-8 py-4 text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#E6C547] transition-colors"
            >
              {t('home.cta')}
              <ArrowRight size={16} strokeWidth={1.8} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
          <div className="absolute bottom-8 right-6 md:right-12 flex flex-col items-center gap-2 text-white/40 text-xs uppercase tracking-[0.3em]">
            <span>{t('home.scroll')}</span>
            <ArrowDown size={14} strokeWidth={1.5} className="animate-bounce" />
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32" data-testid="home-intro">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-start">
          <div className="md:col-span-5">
            <h2 className="font-serif font-light text-white text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1.1]">
              {t('home.intro_title')}
            </h2>
          </div>
          <div className="md:col-span-7 md:pt-4">
            <p className="text-white/70 text-base sm:text-lg leading-relaxed">{t('home.intro_body')}</p>
          </div>
        </div>
      </section>

      {/* Business Areas */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32" data-testid="home-areas">
        <div className="mb-16 max-w-2xl">
          <h2 className="font-serif font-light text-white text-3xl sm:text-4xl lg:text-5xl tracking-tighter">
            {t('home.areas_title')}
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#262626]">
          {areas.map((a, i) => (
            <div
              key={i}
              className="bg-[#0A0A0A] p-10 hover:bg-[#141414] transition-colors duration-500 group"
              data-testid={`area-card-${i}`}
            >
              <div className="text-gold font-serif text-2xl mb-6">0{i + 1}</div>
              <h3 className="font-serif text-white text-xl sm:text-2xl tracking-tight mb-4 group-hover:text-gold transition-colors">
                {a.t}
              </h3>
              <p className="text-white/60 text-sm leading-relaxed">{a.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Advantages with skyline image */}
      <section className="relative" data-testid="home-advantages">
        <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: `url(${SkylineImg})` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-[#0A0A0A]/80 to-[#0A0A0A]" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="mb-16 max-w-2xl">
            <h2 className="font-serif font-light text-white text-3xl sm:text-4xl lg:text-5xl tracking-tighter">
              {t('home.advantages_title')}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {advantages.map((a, i) => (
              <div key={i} className="border-l border-gold/40 pl-6" data-testid={`advantage-${i}`}>
                <h3 className="font-serif text-white text-2xl mb-3">{a.t}</h3>
                <p className="text-white/70 leading-relaxed">{a.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact block */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32" data-testid="home-contact-block">
        <div className="border border-[#262626] p-12 md:p-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <div className="text-xs uppercase tracking-[0.32em] text-gold mb-4">{t('home.get_in_touch')}</div>
            <h3 className="font-serif text-white text-3xl sm:text-4xl tracking-tighter">{t('contact.lead')}</h3>
          </div>
          <Link
            to="/contact"
            data-testid="home-contact-cta"
            className="group inline-flex items-center gap-3 border border-white/20 text-white hover:border-gold hover:text-gold px-8 py-4 text-xs uppercase tracking-[0.2em] font-bold transition-all"
          >
            {t('home.cta')}
            <ArrowRight size={16} strokeWidth={1.8} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
